/**
 * Admin Projects Page
 * 
 * Shows ALL projects across ALL users (admin only).
 * Uses the admin API endpoint which returns projects from all users.
 * Supports filtering by user and search.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import './Admin.css';
import { getAuthToken } from '../../utils/auth';

const CLOUD_API_BASE = process.env.REACT_APP_CLOUD_API_URL || 'http://localhost:8080';

const fetchAdminProjects = async (params) => {
    const token = getAuthToken();
    const queryString = new URLSearchParams(params).toString();
    const response = await fetch(`${CLOUD_API_BASE}/api/admin/projects?${queryString}`, {
        headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!response.ok) throw new Error('Failed to fetch projects');
    return response.json();
};

const fetchUsers = async () => {
    const token = getAuthToken();
    const response = await fetch(`${CLOUD_API_BASE}/api/admin/users?page_size=100`, {
        headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!response.ok) throw new Error('Failed to fetch users');
    return response.json();
};

const deleteProject = async (projectId) => {
    const token = getAuthToken();
    const response = await fetch(`${CLOUD_API_BASE}/api/admin/projects/${projectId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!response.ok) throw new Error('Failed to delete project');
    return response.json();
};

// Icons
const FolderIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
    </svg>
);

const TrashIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <polyline points="3 6 5 6 21 6"/>
        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
    </svg>
);

const OpenIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
        <polyline points="15 3 21 3 21 9"/>
        <line x1="10" y1="14" x2="21" y2="3"/>
    </svg>
);

export function AdminProjectsPage() {
    const navigate = useNavigate();
    const [projects, setProjects] = useState([]);
    const [users, setUsers] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');
    const [userFilter, setUserFilter] = useState('');
    const [isLoading, setIsLoading] = useState(true);

    // Build a user lookup map
    const userMap = {};
    users.forEach(u => { userMap[u.id] = u; });

    const loadProjects = useCallback(async () => {
        setIsLoading(true);
        try {
            const params = { page, page_size: 20 };
            if (search) params.search = search;
            if (userFilter) params.user_id = userFilter;

            const data = await fetchAdminProjects(params);
            setProjects(data.projects || []);
            setTotal(data.total || 0);
        } catch (err) {
            console.error('Failed to load projects:', err);
        } finally {
            setIsLoading(false);
        }
    }, [page, search, userFilter]);

    const loadUsers = useCallback(async () => {
        try {
            const data = await fetchUsers();
            setUsers(data.users || []);
        } catch (err) {
            console.error('Failed to load users for filter:', err);
        }
    }, []);

    useEffect(() => {
        loadProjects();
    }, [loadProjects]);

    useEffect(() => {
        loadUsers();
    }, [loadUsers]);

    const handleSearch = (e) => {
        e.preventDefault();
        setPage(1);
        loadProjects();
    };

    const handleDelete = async (projectId, projectName) => {
        if (!window.confirm(`Permanently delete project "${projectName}"? This cannot be undone.`)) return;
        try {
            await deleteProject(projectId);
            loadProjects();
        } catch (err) {
            console.warn('Failed to delete:', err.message);
        }
    };

    const handleOpenProject = (projectId) => {
        navigate(`/experience/${projectId}`);
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '—';
        return new Date(dateStr).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    return (
        <div className="admin-users">
            <div className="admin-header">
                <h1>All Projects</h1>
                <p>View and manage projects across all users ({total} total)</p>
            </div>

            {/* Filters */}
            <div className="admin-filters">
                <form onSubmit={handleSearch} className="search-form">
                    <input
                        type="text"
                        placeholder="Search by project name..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                    <button type="submit">Search</button>
                </form>

                <select
                    value={userFilter}
                    onChange={(e) => { setUserFilter(e.target.value); setPage(1); }}
                >
                    <option value="">All Users</option>
                    {users.map(u => (
                        <option key={u.id} value={u.id}>
                            {u.display_name || u.email}
                        </option>
                    ))}
                </select>
            </div>

            {/* Projects Table */}
            <div className="admin-table-container">
                {isLoading ? (
                    <div className="table-loading">
                        <div className="spinner"></div>
                    </div>
                ) : projects.length === 0 ? (
                    <div className="admin-empty">
                        <FolderIcon />
                        <p>No projects found</p>
                    </div>
                ) : (
                    <table className="admin-table">
                        <thead>
                            <tr>
                                <th>Project</th>
                                <th>Owner</th>
                                <th>Template</th>
                                <th>Status</th>
                                <th>Created</th>
                                <th>Updated</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {projects.map(project => {
                                const owner = userMap[project.user_id];
                                return (
                                    <tr key={project.id}>
                                        <td>
                                            <div className="user-cell">
                                                <div className="project-thumb-small">
                                                    {project.thumbnail ? (
                                                        <img src={project.thumbnail} alt="" />
                                                    ) : (
                                                        <FolderIcon />
                                                    )}
                                                </div>
                                                <div className="user-info">
                                                    <span className="user-name">
                                                        {project.display_name || project.name}
                                                    </span>
                                                    {project.display_name && project.display_name !== project.name && (
                                                        <span className="user-email">{project.name}</span>
                                                    )}
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            <span className="user-email">
                                                {owner ? (owner.display_name || owner.email) : project.user_id?.slice(0, 8)}
                                            </span>
                                        </td>
                                        <td>
                                            <span className={`tier-badge ${project.template}`}>
                                                {project.template}
                                            </span>
                                        </td>
                                        <td>
                                            <span className={`status-badge ${project.status === 'ACTIVE' ? 'active' : 'inactive'}`}>
                                                {project.status}
                                            </span>
                                        </td>
                                        <td>{formatDate(project.created_at)}</td>
                                        <td>{formatDate(project.updated_at)}</td>
                                        <td>
                                            <div style={{ display: 'flex', gap: '6px' }}>
                                                <button
                                                    className="action-btn"
                                                    onClick={() => handleOpenProject(project.id)}
                                                    title="Open in builder"
                                                >
                                                    <OpenIcon />
                                                </button>
                                                <button
                                                    className="action-btn danger"
                                                    onClick={() => handleDelete(project.id, project.display_name || project.name)}
                                                    title="Delete permanently"
                                                >
                                                    <TrashIcon />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Pagination */}
            <div className="admin-pagination">
                <span>Showing {projects.length} of {total} projects</span>
                <div className="pagination-controls">
                    <button
                        disabled={page === 1}
                        onClick={() => setPage(p => p - 1)}
                    >
                        Previous
                    </button>
                    <span>Page {page}</span>
                    <button
                        disabled={projects.length < 20}
                        onClick={() => setPage(p => p + 1)}
                    >
                        Next
                    </button>
                </div>
            </div>
        </div>
    );
}

export default AdminProjectsPage;

